import {
  Button,
  IconRubbishBin,
  IconSave,
  IconSettings,
  IconEdit,
  Skeleton,
  Box,
  SkeletonWithAnimation,
  Inline,
  IconCopy,
} from '@ui-lib';
import TopNav, { AlignedContent, PageOptions } from '@components/TopNav/TopNav';
import { faFolderUpload, faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faCheck, faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import Tooltip from '@components/Tooltip/Tooltip';
import { useRouter } from 'next/router';
import { NotificationProps, showNotification } from 'ui-lib/mantine/notifications';
import { useState, useCallback, ReactNode } from 'react';
import { useWorkspaceUrls } from '@modules/use-workspace-urls';
import { notifications } from '@mantine/notifications';
import { analytics } from '@modules/analytics';

export const CardHeaderNavLoadingPlaceholder = (): JSX.Element => (
  <SkeletonWithAnimation>
    <TopNav>
      <Box width="full" display="flex" justifyContent="spaceBetween">
        <Inline gap="xsmall">
          <Skeleton height={44} width={132} />
          <Skeleton height={44} width={120} />
        </Inline>
        <Inline gap="xsmall">
          <Skeleton height={44} width={98} />
          <Skeleton height={44} width={123} />
        </Inline>
      </Box>
    </TopNav>
  </SkeletonWithAnimation>
);

type CardHeaderNavProps = {
  isEditing?: boolean;
  showEditButton?: boolean;
  showSettingsButton?: boolean;
  showDeleteButton?: boolean;
  showOptionButtons?: boolean;
  showShareButton?: boolean;
  showSendAllEmailsButton?: boolean;
  canSave?: boolean;
  isUnpublished?: boolean;
  duplicateButton?: {
    onClick: () => Promise<void>;
    isWorking: boolean;
    label?: string;
    hidden?: boolean;
  };
  /** Tooltip message for the save button in the card edit page. Message displayed when save button is disabled */
  saveDisabledMessage?: string | ReactNode;
  onClickBulkUpload?: () => void;
  onClickSendAllEmails?: () => void;
  onClickDiscardChanges?: () => void;
  onClickDelete?: () => void;
  onClickEdit?: () => void;
  onClickPublish?: () => void;
  onClickSettings?: () => void;
  onClickShare?: () => void;
  showSyncUpdatesWarning?: () => void;
  onSyncUpdatesChange?: (syncUpdates: boolean) => void;
  shareCardBtnRef?: (instance: HTMLDivElement | null) => void;
};

export function CardHeaderNav({
  isEditing,
  showEditButton = true,
  showSettingsButton,
  showDeleteButton,
  showOptionButtons,
  showShareButton,
  showSendAllEmailsButton,
  canSave = true,
  isUnpublished = false,
  saveDisabledMessage,
  onClickBulkUpload,
  onClickSendAllEmails,
  onClickDiscardChanges,
  onClickDelete,
  duplicateButton,
  onClickEdit,
  onClickPublish,
  onClickSettings,
  onClickShare,
  showSyncUpdatesWarning,
  onSyncUpdatesChange,
  shareCardBtnRef,
}: CardHeaderNavProps): JSX.Element {
  const [discardLoading, setDiscardLoading] = useState<boolean>(false);
  const [publishLoading, setPublishLoading] = useState<boolean>(false);
  const [loadingOnEdit, setLoadingOnEdit] = useState(false);
  const router = useRouter();
  const { bulkUploadUrl } = useWorkspaceUrls();

  const searchParams = router.query;
  const redirectToBulkUpload = searchParams.redirectToBulkUpload === 'true';

  const handleClickEdit = useCallback(() => {
    setLoadingOnEdit(true);
    onClickEdit?.();
  }, [onClickEdit]);

  async function handleDiscard() {
    setDiscardLoading(true);
    await onClickDiscardChanges?.();
    setDiscardLoading(false);
  }

  async function handleRedirectNotification(redirectToBulkUpload: boolean) {
    if (!redirectToBulkUpload) {
      return;
    }

    const notificationId = 'return-to-bulk-upload-notification';

    const notificationProps: NotificationProps = {
      id: notificationId,
      notificationContent: {
        content: 'Template created, continue with bulk upload',
        buttonIcon: <FontAwesomeIcon icon={faRotateLeft} />,
        buttonContent: 'Return to bulk upload',
        buttonOnClick: () => {
          analytics.track({
            name: 'bulkUpload_step1_createTemplateReturn_clicked',
          });

          router.push(bulkUploadUrl);
          notifications.hide(notificationId);
        },
      },
      tone: 'positive',
      onClose: () => {
        analytics.track({
          name: 'bulkUpload_step1_createTemplateReturn_Dismissed',
        });
      },
    };

    showNotification(notificationProps);
  }

  async function handlePublish() {
    setPublishLoading(true);
    await onClickPublish?.();
    setPublishLoading(false);
    handleRedirectNotification(redirectToBulkUpload);
  }

  return (
    <TopNav>
      <AlignedContent align="left">
        <PageOptions>
          {onClickBulkUpload && (
            <Button
              onClick={onClickBulkUpload}
              size="regular"
              variant="outline"
              iconLeading={<FontAwesomeIcon icon={faFolderUpload} />}
            >
              Bulk Upload
            </Button>
          )}

          {showSendAllEmailsButton && (
            <Button
              onClick={onClickSendAllEmails}
              size="regular"
              variant="outline"
              iconLeading={<FontAwesomeIcon icon={faEnvelope} />}
            >
              Send all activation emails
            </Button>
          )}

          {!isEditing && showEditButton && showOptionButtons && (
            <Button
              onClick={handleClickEdit}
              loading={loadingOnEdit}
              size="regular"
              variant="solid"
              iconLeading={<IconEdit tone="neutralInverted" />}
            >
              Edit
            </Button>
          )}

          {!isEditing && showOptionButtons && !!duplicateButton && !duplicateButton.hidden && (
            <Button
              onClick={duplicateButton.onClick}
              loading={duplicateButton.isWorking}
              size="regular"
              variant="outline"
              iconLeading={<IconCopy />}
            >
              {duplicateButton.label || 'Create a copy'}
            </Button>
          )}

          {showOptionButtons && showSettingsButton && (
            <Button onClick={onClickSettings} size="regular" variant="outline" iconLeading={<IconSettings />}>
              Settings
            </Button>
          )}

          {showDeleteButton && showOptionButtons && onClickDelete ? (
            <Button onClick={onClickDelete} size="regular" variant="outline" iconLeading={<IconRubbishBin />}>
              Delete
            </Button>
          ) : null}
        </PageOptions>
      </AlignedContent>

      {/* edit mode specific buttons */}
      {isEditing && (
        <AlignedContent align="right">
          <Button size="regular" variant="outline" onClick={handleDiscard} loading={discardLoading}>
            {isUnpublished ? 'Cancel' : 'Discard changes'}
          </Button>

          {!canSave && saveDisabledMessage ? (
            <Tooltip aria-label="" label={saveDisabledMessage}>
              <div>
                <Button
                  size="regular"
                  variant="solid"
                  iconLeading={<IconSave size="large" />}
                  onClick={handlePublish}
                  loading={publishLoading}
                  disabled={true}
                >
                  {isUnpublished ? 'Create' : 'Save'}
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button
              size="regular"
              variant="solid"
              iconLeading={<IconSave size="large" />}
              onClick={handlePublish}
              loading={publishLoading}
              data-testid="publish-card-button"
              disabled={false}
            >
              {isUnpublished ? 'Create' : 'Save'}
            </Button>
          )}
        </AlignedContent>
      )}

      {/* share card button */}
      {showShareButton && (
        <AlignedContent align="right">
          <div ref={shareCardBtnRef}>
            <Button
              size="regular"
              variant="solid"
              iconLeading={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
              onClick={onClickShare}
            >
              Share card
            </Button>
          </div>
        </AlignedContent>
      )}
    </TopNav>
  );
}
